import { useSuspenseQuery } from '@apollo/client';
import type { FC } from 'react';
import { Suspense } from 'react';

import {
  ModuleHeader,
  EmptyState,
  WarningMessageBox,
} from '@xing-com/crate-entity-pages-common';
import { useEditContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { useModuleContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-module-context';
import {
  Location,
  EntityPagesModuleLocationQueryDocument,
  MapIcon,
} from '@xing-com/crate-entity-pages-location';

import { LocationSkeleton } from './locations-module.skeleton';

const LocationsModuleInner: FC = () => {
  const { isEditing } = useEditContext();
  const { pageContext } = usePageContext();
  const { moduleContext } = useModuleContext();

  const pageSlug = pageContext.pageSlug;

  const { data, error, refetch } = useSuspenseQuery(
    EntityPagesModuleLocationQueryDocument,
    {
      variables: { id: pageContext.pageSlug },
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    }
  );

  const mainLocation = data?.entityPageLocations?.collection.find(
    (location) => location.highlighted === true
  );

  return (
    <>
      <ModuleHeader
        editTestId="EDIT_BUTTON_ON_LOCATIONS"
        headlineCopyKey="EP_LOCATIONS_HEADLINE"
        editTo={`${pageContext.basePath}/${pageSlug}/edit/locations`}
      />
      {error ? (
        <WarningMessageBox
          headerText="EP_ERROR_HEADER"
          bodyText="EP_ERROR_BODY"
          buttonText="EP_ERROR_RELOAD_CTA"
          onClick={() => refetch()}
        />
      ) : (
        <>
          {mainLocation ? (
            <Location
              label={mainLocation.label}
              city={mainLocation.city}
              address={mainLocation.address}
              postcode={mainLocation.postcode}
              country={mainLocation.country}
              coordinates={mainLocation.coordinates}
              email={mainLocation.email}
              phoneNumber={mainLocation.phoneNumber}
              faxNumber={mainLocation.faxNumber}
              websiteURL={mainLocation.websiteURL}
              intersected={!!moduleContext?.intersected}
              trackingModuleType="Main"
            />
          ) : null}
          {!mainLocation && isEditing ? (
            <EmptyState
              icon={MapIcon}
              buttonProps={{
                to: `/pages/${pageSlug}/edit/locations`,
                copyKey: 'EP_LOCATION_EMPTY_STATE_BUTTON',
              }}
              headlineCopyKey="EP_LOCATION_EMPTY_STATE_TITLE"
              textCopyKey="EP_LOCATION_EMPTY_STATE_TEXT"
            />
          ) : null}
        </>
      )}
    </>
  );
};

const Fallback: FC = () => {
  return (
    <>
      <ModuleHeader headlineCopyKey="EP_LOCATIONS_HEADLINE" />
      <LocationSkeleton />
    </>
  );
};

export const LocationsModule: FC = () => {
  return (
    <Suspense fallback={<Fallback />}>
      <LocationsModuleInner />
    </Suspense>
  );
};
